<template>
  <NBackdrop @outsideClick="closeOnOutside && (showSidebar = false)">
    <NSidebarTransition @afterLeave="onAfterLeave">
      <NSidebarContent
        v-if="showSidebar"
        v-bind="$props"
        @close="showSidebar = false"
        @openMenu="$emit('openMenu', $event)"
        @back="$emit('back')"
        class="sidebar-modal"
        :class="{ transition: !isDragging }"
        :size="size"
        ref="sidebar"
        @touchstart="onTouchStart($event)"
        @touchmove="onTouchMove($event)"
        @touchend="onTouchEnd"
      >
        <slot />
      </NSidebarContent>
    </NSidebarTransition>
  </NBackdrop>
</template>

<script lang="ts">
import NBackdrop from '@/modules/global/components/core/NBackdrop.vue'
import NSidebarContent from './NSidebarContent.vue'
import NSidebarTransition from './NSidebarTransition.vue'
import { isMobile } from '@/util/dom-utils'
import { Component, Prop, Ref, Vue } from 'vue-property-decorator'

@Component({
  name: 'n-sidebar',
  components: { NSidebarTransition, NBackdrop, NSidebarContent }
})
export default class NSidebar extends Vue {
  @Prop({ default: '' }) title!: string
  @Prop({ default: false, type: Boolean }) showMenu!: boolean
  @Prop({ default: true, type: Boolean }) closeOnOutside?: boolean
  @Prop({ default: true, type: Boolean }) closeOnEscape?: boolean
  @Prop({ default: false, type: Boolean }) isScrolling?: boolean
  @Prop({
    type: String,
    validator: (prop: string) => ['m', 'l'].includes(prop),
    default: 'm'
  })
  size?: String

  @Prop() showBackButton!: boolean
  @Prop({ default: undefined }) backButtonLabel!: string

  @Ref('sidebar') sidebarRef!: NSidebarContent

  private showSidebar = false

  private isDragging = false
  private startY = 0
  private translateY = 0

  get modalHeight() {
    return this.sidebarRef.bodyRef.clientHeight
  }

  mounted() {
    this.showSidebar = true
    if (this.closeOnEscape) {
      window.addEventListener('keydown', this.onEscape)
    }
    if (isMobile()) {
      this.disableBodyScrolling()
    }
  }

  destroyed() {
    if (this.closeOnEscape) {
      window.removeEventListener('keydown', this.onEscape)
    }
    if (isMobile()) {
      this.enableBodyScrolling()
    }
  }

  onEscape(ev: KeyboardEvent) {
    if (ev.key === 'Escape') {
      this.$emit('close')
    }
  }

  onAfterLeave() {
    this.$emit('close')
  }

  onTouchStart(event: TouchEvent) {
    if (isMobile()) {
      this.isDragging = true
      this.startY = event.touches[0].clientY
    }
  }

  onTouchMove(event: TouchEvent) {
    if (isMobile()) {
      if (!this.isScrolling && !this.isDragging) {
        this.startY = event.touches[0].clientY
        this.isDragging = true
      }

      if (this.isDragging) {
        const deltaY = event.touches[0].clientY - this.startY

        this.translateY = Math.max(0, deltaY)
        this.transform()
      }
    }
  }

  onTouchEnd() {
    if (isMobile()) {
      if (this.isDragging) {
        this.isDragging = false
        const heightBreakOff = this.modalHeight * 0.5
        if (this.translateY > heightBreakOff) {
          this.showSidebar = false
        } else {
          this.translateY = 0
          this.transform()
        }
      }
    }
  }

  transform() {
    const input = this.sidebarRef.bodyRef
    input.style.transform = `translateY(${this.translateY}px)`
  }

  disableBodyScrolling() {
    const bodyStyle = document.body.style
    const htmlStyle = document.documentElement.style
    bodyStyle.overflow = 'hidden'
    bodyStyle.position = 'fixed'
    bodyStyle.right = '0'
    bodyStyle.left = '0'
    bodyStyle.bottom = '0'
    bodyStyle.top = '0'
    bodyStyle.overscrollBehavior = 'none' // For Chrome browsers
    htmlStyle.overscrollBehavior = 'none' // For Safari browsers
  }

  enableBodyScrolling() {
    const bodyStyle = document.body.style
    const htmlStyle = document.documentElement.style
    bodyStyle.overflow = 'auto'
    bodyStyle.position = 'static'
    bodyStyle.overscrollBehavior = 'auto'
    htmlStyle.overscrollBehavior = 'auto'
  }
}
</script>

<style scoped lang="scss">
@use '@/assets/styles' as *;

.sidebar-modal {
  margin-left: auto;
  height: 100%;

  &.m {
    @include mobile {
      position: absolute;
      bottom: 0;

      width: 100%;
      height: 92%;

      border-top-left-radius: $notch-radius-4x;
      border-top-right-radius: $notch-radius-4x;
    }

    @include tablet {
      width: 70%;
    }
  }

  &.l {
    @include mobile {
      position: absolute;
      bottom: 0;

      width: 100%;
      height: 92%;

      border-top-left-radius: $notch-radius-4x;
      border-top-right-radius: $notch-radius-4x;
    }

    @include tablet {
      width: 70%;
    }
  }

  &.transition {
    transition: all 0.2s ease-in;
  }
}
</style>
